import { ValidateJSModel } from 'forms/shared/types';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import {
  frequencyOfContact, methodsOfContact,
  relativeTypes, residencyStatus
} from 'constants/enums/relationshipOptions';
import {
  DEFAULT_EARLIEST, DEFAULT_FUTURE_LATEST, DEFAULT_LATEST
} from 'constants/dateLimits';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { hasYesOrNo } from 'models/validate';
import alias from 'forms/shared/validators/models/alias/alias';
import { REQUIRED } from 'constants/errors';
import { getCountryValue, isSafeUnitedStates } from 'helpers/locations';
import pvqLocationBirthplaceModelValidator from 'forms/shared/validators/models/location/models/pvqBirthplace';
import { DeepSafePartial } from 'types/types';
import { affiliation } from 'forms/shared/validators/models/affiliation/affiliation';
import {
  PVQ_CITIZEN, PVQ_DERIVED, PVQ_FOREIGN_BORN, PVQ_NATIONAL_BY_BIRTH_IN_US, PVQ_NATIONAL_BY_BIRTH_OUTSIDE_US, PVQ_NATURALIZED, PVQ_NOT_CITIZEN, isNationalOrNonCitizen,
  isNotUSCLivesInUS,
  isNotUSCLivesOutsideUS
} from 'constants/enums/citizenshipOptions';
import {
  DOC_DerivedCertificate, DOC_DerivedMriv, DOC_DerivedPermanentAlien, DOC_NaturalizedCertificate, DOC_NaturalizedMriv, DOC_NaturalizedPermanentAlien, DOC_NonArrival, DOC_NonEmployment, DOC_NonF1, DOC_NonJ1, DOC_NonMRIV, DOC_NonPermanent, DOC_NonVisa, DOC_Other, relationshipDerivedDocuments, relationshipNaturalizedDocuments, relationshipNonCitizenDocuments, relationshipUsForeignDocuments
} from 'constants/enums/foreignBornDocumentCivlTypes';
import { RelationshipsRelativeItemModel } from '../types';

// The PVQ Relatives subsection is separated from SF - unnecessary to check for formType isPVQ here.
// in /src/forms/sections/relationships/relatives/validate.ts you can see differentiation on line 24
type Attributes = DeepSafePartial<RelationshipsRelativeItemModel>;
const hasExpirationDate = (
  attributes: Attributes,
) => {
  const status = attributes.CitizenshipStatus?.value;
  const documentation = attributes.CitizenshipDocumentation?.value;

  return (
    (status === PVQ_FOREIGN_BORN && documentation === DOC_Other)
    || (status === PVQ_NATURALIZED && documentation === DOC_Other)
    || (status === PVQ_DERIVED && documentation === DOC_Other)
    || (isNationalOrNonCitizen(status))
  );
};

const isDeceasedOrUnknown = (
  attributes: Attributes,
): boolean => (attributes.IsDeceased?.value !== 'No');

export const relationshipsPvqRelativeItemValidator: ValidateJSModel<RelationshipsRelativeItemModel> = {
  PvqRelation: {
    presence: true,
    hasValue: {
      validator: {
        inclusion: relativeTypes,
      }
    }
  },

  Name: (value, attributes) => ({
    presence: true,
    model: { validator: pvqNameModelValidator },
  }),

  NoContactWithFamily: {},
  Relation: {},

  Birthdate: (value, attributes) => {
    if (attributes.BirthdateNotApplicable?.applicable !== false) {
      return {
        presence: true,
        date: {
          earliest: DEFAULT_EARLIEST,
          latest: DEFAULT_LATEST,
          tooEarly: 'error.birthdateTooFar.min'
        }
      };
    }
    return {};
  },

  Birthplace: (value, attributes) => {
    if (attributes.BirthplaceNotApplicable?.applicable !== false) {
      return {
        presence: true,
        location: {
          validator: pvqLocationBirthplaceModelValidator,
          requireCounty: true,
        },
      };
    } return {};
  },

  Citizenship: (value, attributes) => {
    if (attributes.CitizenshipCountryNotApplicable?.applicable !== false) {
      return {
        hasValue: true,
        country: true,
      };
    }
    return {};
  },
  IsDeceased: {
    presence: true,
    hasValue: { validator: hasYesOrNo },
  },
  MaidenName: {},

  Aliases: (value, attributes) => {
    if (!isDeceasedOrUnknown(attributes)) {
      return {
        presence: true,
        branchCollection: {
          validator: alias,
        },
      };
    } return {};
  },

  Address: (value, attributes) => {
    if (!isDeceasedOrUnknown(attributes)) {
      if (attributes.InUsaNotApplicable?.applicable !== false) {
        return {
          presence: true,
          location: {
            validator: pvqLocationModelValidator,
            requireStreet: true,
            requireIsMilitaryDiplomaticAddress: true,
            requireMilitaryDiplomaticInstallationName: true,
            requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
          },
        };
      }
    }

    return {};
  },

  CitizenshipStatus: (value, attributes) => {
    if ((!isDeceasedOrUnknown(attributes)) && attributes.ParentCitizenshipStatusNotApplicable?.applicable !== false) {
      return {
        presence: true,
        hasValue: {
          validator: {
            inclusion: [PVQ_CITIZEN, PVQ_FOREIGN_BORN, PVQ_NATURALIZED, PVQ_DERIVED, PVQ_NOT_CITIZEN, PVQ_NATIONAL_BY_BIRTH_IN_US, PVQ_NATIONAL_BY_BIRTH_OUTSIDE_US],
          },
        }
      };
    }

    return {};
  },

  CitizenshipCountryNotApplicable: {},
  AlternateAddress: {},
  AlternateAddressExplanation: {},

  CitizenshipDocumentation: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (attributes.CitizenshipStatus?.value === PVQ_FOREIGN_BORN) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: relationshipUsForeignDocuments
            }
          },
        },
      };
    }
    if (attributes.CitizenshipStatus?.value === PVQ_NATURALIZED) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: relationshipNaturalizedDocuments
            }
          },
        },
      };
    }
    if (attributes.CitizenshipStatus?.value === PVQ_DERIVED) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: relationshipDerivedDocuments
            }
          },
        },
      };
    }
    if (isNotUSCLivesInUS(attributes.CitizenshipStatus?.value, attributes.Address?.country === 'United States')) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: relationshipNonCitizenDocuments
            }
          },
        },
      };
    }

    return {};
  },

  DocumentNumber: (value, attributes) => {
    if ((isDeceasedOrUnknown(attributes)) || attributes.DocumentNumberNotApplicable?.applicable === false || attributes.NoDocumentNumber?.checked === true) { // eslint-disable-line max-len
      return {};
    }

    if (attributes.CitizenshipStatus?.value === PVQ_FOREIGN_BORN && attributes.CitizenshipDocumentation?.value !== DOC_Other) {
      return {
        hasValue: true,
      };
    }

    if (attributes.CitizenshipStatus?.value === PVQ_NATURALIZED) {
      if (attributes.CitizenshipDocumentation?.value === DOC_NaturalizedCertificate
        || attributes.CitizenshipDocumentation?.value === DOC_NaturalizedPermanentAlien
        || attributes.CitizenshipDocumentation?.value === DOC_NaturalizedMriv) {
        return {
          hasValue: true,
        };
      }
    }

    if (attributes.CitizenshipStatus?.value === PVQ_DERIVED) {
      if (attributes.CitizenshipDocumentation?.value === DOC_DerivedCertificate
        || attributes.CitizenshipDocumentation?.value === DOC_DerivedPermanentAlien
        || attributes.CitizenshipDocumentation?.value === DOC_DerivedMriv) {
        return {
          hasValue: true,
        };
      }
    }

    if (isNationalOrNonCitizen(attributes.CitizenshipStatus?.value) && (
      attributes.CitizenshipDocumentation?.value === DOC_NonPermanent
      || attributes.CitizenshipDocumentation?.value === DOC_NonEmployment
      || attributes.CitizenshipDocumentation?.value === DOC_NonMRIV
      || attributes.CitizenshipDocumentation?.value === DOC_NonArrival
      || attributes.CitizenshipDocumentation?.value === DOC_NonVisa
      || attributes.CitizenshipDocumentation?.value === DOC_NonF1
      || attributes.CitizenshipDocumentation?.value === DOC_NonJ1)) {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  CertificateNumber: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (attributes.CitizenshipStatus?.value === PVQ_NATURALIZED) {
      if (attributes.CitizenshipDocumentation?.value === DOC_NaturalizedCertificate) {
        if (attributes.CertificateNumberNotApplicable?.applicable !== false) {
          return {
            hasValue: true,
          };
        }
      }
    }
    if (attributes.CitizenshipStatus?.value === PVQ_DERIVED) {
      if (attributes.CitizenshipDocumentation?.value === DOC_DerivedCertificate) {
        if (attributes.CertificateNumberNotApplicable?.applicable !== false) {
          return {
            hasValue: true,
          };
        }
      }
    }

    return {};
  },

  CourtName: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (attributes.CitizenshipStatus?.value === PVQ_NATURALIZED) {
      if (attributes.CitizenshipDocumentation?.value === DOC_NaturalizedCertificate) {
        if (attributes.CourtNameNotApplicable?.applicable !== false) {
          return {
            hasValue: true,
          };
        }
      }
    } return {};
  },

  CourtAddress: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (attributes.CitizenshipStatus?.value === PVQ_NATURALIZED) {
      if (attributes.CitizenshipDocumentation?.value === DOC_NaturalizedCertificate) {
        if (attributes.CourtAddressNotApplicable?.applicable !== false) {
          return {
            presence: true,
            location: {
              validator: pvqLocationModelValidator,
              requireStreet: true,
              requireIsMilitaryDiplomaticAddress: false,
              requireMilitaryDiplomaticInstallationName: false,
              requireApoFpoDpoPostcode: false,
            },
          };
        }
      }
    } return {};
  },

  Residency: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (isNotUSCLivesInUS(attributes.CitizenshipStatus?.value, attributes.Address?.country === 'United States')) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: residencyStatus
            }
          },
        },
      };
    } return {};
  },

  AddressExplanation: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (attributes.Residency?.value === 'Other'
      && isNotUSCLivesInUS(attributes.CitizenshipStatus?.value, attributes.Address?.country === 'United States')) {
      return {
        hasValue: true,
      };
    } return {};
  },

  OtherCitizenshipDocumentation: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (attributes.CitizenshipStatus?.value !== PVQ_CITIZEN && attributes.CitizenshipDocumentation?.value === DOC_Other) {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  ResidenceDocumentNumber: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (attributes.IsDeceased?.value === 'No'
      && attributes.CitizenshipStatus?.value !== PVQ_CITIZEN
      && attributes.CitizenshipDocumentation?.value === DOC_Other
      && attributes.DocumentNumberNotApplicable?.applicable !== false
      && attributes.NoDocumentNumber?.checked !== true) {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  Expiration: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)
      || isNotUSCLivesOutsideUS(attributes.CitizenshipStatus?.value, getCountryValue(attributes.Address?.country))) {
      return {};
    }

    if (attributes.CitizenshipStatus?.value !== PVQ_CITIZEN && attributes.ParentCitizenshipStatusNotApplicable?.applicable !== false) {
      if (attributes.ExpirationUnknown?.applicable !== false && attributes.ExpirationNotApplicable?.applicable !== false && hasExpirationDate(attributes)) {
        return {
          presence: true,
          date: {
            earliest: DEFAULT_EARLIEST,
            latest: DEFAULT_FUTURE_LATEST,
          }
        };
      }
    } return {};
  },

  Document: {},
  DocumentComments: {},
  OptionalComments: {},

  InContact: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    return {
      hasValue: true,
    };
  },

  FirstContact: (value, attributes, attributeName, options) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    const { applicantBirthdate } = options;
    if (attributes.InContact?.value === 'Yes') {
      return {
        presence: true,
        date: { earliest: applicantBirthdate, latest: DEFAULT_LATEST }
      };
    }

    return {};
  },

  LastContact: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (attributes.InContact?.value === 'Yes') {
      return {
        presence: true,
        date: {
          earliest: attributes.FirstContact,
          tooEarly: 'error.relativeLastContact',
          latest: DEFAULT_LATEST,
        }
      };
    }

    return {};
  },

  Methods: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (attributes.InContact?.value === 'Yes') {
      return {
        presence: true,
        array: {
          validator: {
            presence: true,
            inclusion: methodsOfContact,
          },
          length: { minimum: 1, message: REQUIRED },
        },
      };
    } return {};
  },

  MethodsComments: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (attributes.InContact?.value === 'Yes' && attributes.Methods?.values?.includes('Other')) {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  Frequency: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (attributes.InContact?.value === 'Yes') {
      return {
        hasValue: {
          validator: {
            inclusion: frequencyOfContact
          },
        },
      };
    } return {};
  },

  FrequencyComments: {},
  FirstContactExplanation: {},

  FrequencyExplanation: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (
      attributes.InContact?.value === 'Yes'
      && attributes.Frequency?.value === 'Other') {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  Employer: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (attributes.EmployerNotApplicable?.applicable !== false && attributes?.NeverWorked?.checked !== true) {
      return {
        hasValue: true,
      };
    }

    return {};
  },

  RelativeOccupation: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (attributes?.Employer?.value !== '' && attributes?.EmployerNotApplicable?.applicable !== false && !attributes?.NeverWorked?.checked) {
      return {
        hasValue: true,
      };
    } return {};
  },

  EmployerAddress: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    if (
      attributes.EmployerNotApplicable?.applicable !== false
      && attributes.EmployerAddressNotApplicable?.applicable !== false
      && attributes?.NeverWorked?.checked !== true
    ) {
      return {
        presence: true,
        location: {
          validator: pvqLocationModelValidator,
          requireStreet: isSafeUnitedStates(value),
          requireIsMilitaryDiplomaticAddress: true,
          requireMilitaryDiplomaticInstallationName: true,
          requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
        },
      };
    } return {};
  },

  HasRelativeAffiliations: (value, attributes) => {
    if (isDeceasedOrUnknown(attributes)) {
      return {};
    }

    return {
      presence: true,
      branchCollection: {
        validator: affiliation,
      },
    };
  },
  HasAffiliation: {},
  PvqEmployerRelationship: {},
  PvqOtherGovtAgencyExplanation: {},

  DocumentNumberExplanation: {},
  ParentCitizenshipStatusNotApplicable: {},
  EntryComments: {},
  EntryExplanation: {},
  MaidenSameAsListed: {},
  DocumentNumberNotApplicable: {},
  EmployerNotApplicable: {},
  EmployerAddressNotApplicable: {},
  EmployerRelationshipNotApplicable: {},
  AlternateAddressNotApplicable: {},
  ParentNotApplicable: {},
  MothersMaidenNameComments: {},
  AliasesComments: {},
  AliasesSummaryComments: {},
  DeceasedComments: {},
  ForeignRelativeComments: {},
  EmployerComments: {},
  HasAffiliationComments: {},
  ForeignAffiliationComments: {},
  AnswerComments: {},
  AddressComments: {},
  PhysicalLocationComments: {},
  AddressNotApplicable: {},
  CourtAddressNotApplicable: {},
  CourtAddressExplanation: {},
  FirstContactNotApplicable: {},
  EmployerRelationship: {},
  NoDocumentNumber: {},
};
